import type { ContactSalesFormValues, ValidationMessages } from "./types";
import { isPersonalEmail, isExpectedMonthlyUsageCountry } from "./utils";

type ValidatorFieldApi<TFormData, TFieldName extends keyof TFormData> = {
  form: {
    getFieldValue: <K extends keyof TFormData>(field: K) => TFormData[K];
  };
};

export const createFieldValidators = (messages: ValidationMessages) => ({
  website: ({ value }: { value: string }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    if (value.length > 255) {
      return messages.maxLength(255);
    }
    if (!/^(https?:\/\/)?[\w.-]+\.\w+.*$/.test(value)) {
      return messages.url;
    }
    return undefined;
  },

  email: ({ value }: { value: string }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    if (value.length > 254) {
      return messages.maxLength(254);
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return messages.email;
    }
    return undefined;
  },

  first_name: ({ value }: { value: string }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    if (value.length > 100) {
      return messages.maxLength(100);
    }
    return undefined;
  },

  last_name: ({ value }: { value: string }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    if (value.length > 100) {
      return messages.maxLength(100);
    }
    return undefined;
  },

  company: ({ value, fieldApi }: { value: string; fieldApi: ValidatorFieldApi<ContactSalesFormValues, "company"> }) => {
    const email = fieldApi.form.getFieldValue("email") as string;
    const isRequired = email && isPersonalEmail(email);

    if (isRequired && (!value || value.trim().length === 0)) {
      return messages.required;
    }
    if (value && value.length > 200) {
      return messages.maxLength(200);
    }
    return undefined;
  },

  company_size: ({
    value,
    fieldApi,
  }: {
    value: string;
    fieldApi: ValidatorFieldApi<ContactSalesFormValues, "company_size">;
  }) => {
    const email = fieldApi.form.getFieldValue("email") as string;
    const isRequired = email && isPersonalEmail(email);

    if (isRequired && (!value || value.trim().length === 0)) {
      return messages.required;
    }
    return undefined;
  },

  industry: ({
    value,
    fieldApi,
  }: {
    value: string;
    fieldApi: ValidatorFieldApi<ContactSalesFormValues, "industry">;
  }) => {
    const email = fieldApi.form.getFieldValue("email") as string;
    const isRequired = email && isPersonalEmail(email);

    if (isRequired && (!value || value.trim().length === 0)) {
      return messages.required;
    }
    return undefined;
  },

  country: ({ value }: { value: string }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    return undefined;
  },

  phone_number: ({
    value,
    fieldApi,
  }: {
    value: string;
    fieldApi: ValidatorFieldApi<ContactSalesFormValues, "phone_number">;
  }) => {
    const country = fieldApi.form.getFieldValue("country");
    const isRequired = country !== "United States of America";

    if (isRequired && (!value || value.trim().length === 0)) {
      return messages.required;
    }

    if (value && value.trim() !== "") {
      if (value.length > 50) {
        return messages.maxLength(50);
      }

      // Check format: only allow digits, spaces, parens, dashes, dots, and leading +
      if (!/^\+?[\d\s().\-]+$/.test(value)) {
        return messages.phone;
      }

      // Check minimum digits (ITU-T E.164: min 7 digits for national number)
      const digitCount = value.replace(/\D/g, "").length;
      if (digitCount < 7) {
        return messages.phone;
      }
    }

    return undefined;
  },

  detail: ({ value }: { value: string }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    if (value.length > 5000) {
      return messages.maxLength(5000);
    }
    return undefined;
  },

  how_did_you_hear: ({ value }: { value: string }) => {
    if (value && value.length > 200) {
      return messages.maxLength(200);
    }
    return undefined;
  },

  expected_monthly_usage: ({
    value,
    fieldApi,
  }: {
    value: string;
    fieldApi: ValidatorFieldApi<ContactSalesFormValues, "expected_monthly_usage">;
  }) => {
    const country = fieldApi.form.getFieldValue("country");
    const isRequired = isExpectedMonthlyUsageCountry(country);

    if (isRequired && (!value || value.trim().length === 0)) {
      return messages.required;
    }
    return undefined;
  },
});
